import type { AsyncCallback, AsyncState } from '$lib/types';
import { AsyncStatus } from '$lib/enums';
import { useAlive } from './use-alive.svelte';

export type AsyncCallbackHook<TData, TError, TArgs extends unknown[]> = {
	readonly state: AsyncState<TData, TError>;
	call: AsyncCallback<TArgs, TData>;
	reset: () => void;
};

/**
 * Tracks the state of an async operation. Only the latest call may settle the state: an older
 * request that resolves after a newer one started rejects with "Request replaced!".
 * Must be called during component init.
 */
export function useAsyncCallback<TData, TError = unknown, TArgs extends unknown[] = []>(
	asyncCallback: AsyncCallback<TArgs, TData>
): AsyncCallbackHook<TData, TError, TArgs> {
	let state = $state.raw<AsyncState<TData, TError>>({ status: AsyncStatus.IDLE });
	let reqNumber = 0;
	const alive = useAlive();

	const call: AsyncCallback<TArgs, TData> = async (...args) => {
		state = { status: AsyncStatus.LOADING };
		reqNumber += 1;
		const currentReqNumber = reqNumber;

		try {
			const data = await asyncCallback(...args);
			if (currentReqNumber !== reqNumber) {
				throw new Error('AsyncCallbackHook: Request replaced!');
			}
			if (alive()) state = { status: AsyncStatus.SUCCESS, data };
			return data;
		} catch (e) {
			if (currentReqNumber !== reqNumber) {
				throw new Error('AsyncCallbackHook: Request replaced!', { cause: e });
			}
			if (alive()) state = { status: AsyncStatus.ERROR, error: e as TError };
			throw e;
		}
	};

	return {
		get state() {
			return state;
		},
		call,
		reset() {
			reqNumber += 1;
			state = { status: AsyncStatus.IDLE };
		}
	};
}

/** `useAsyncCallback` that runs once on mount; the result is read from `state`. */
export function useAsyncCallbackValue<TData, TError = unknown>(
	asyncCallback: AsyncCallback<[], TData>
): AsyncCallbackHook<TData, TError, []> {
	const hook = useAsyncCallback<TData, TError, []>(asyncCallback);
	$effect(() => {
		hook.call().catch(() => undefined);
	});
	return hook;
}
